import React, { createContext, useContext, ReactNode } from 'react';
import { useOptimizedSupabaseScripts, Script, ScriptTemplate, type ScriptInsert } from '@/hooks/useOptimizedSupabaseScripts';
import { useSupabaseProspectLists, ProspectList, type ProspectListInsert, Prospect } from '@/hooks/useSupabaseProspectLists';

interface OptimizedCampaignDataContextType {
  // Scripts
  scripts: Script[];
  templates: ScriptTemplate[];
  scriptsLoading: boolean;
  addScript: (scriptData: Omit<ScriptInsert, 'id' | 'user_id' | 'created_at' | 'updated_at'>) => Promise<Script>;
  updateScript: (id: string, updates: Partial<Script>) => Promise<Script>;
  deleteScript: (id: string) => Promise<void>;
  refreshScripts: () => Promise<void>;
  getIndustries: () => string[];
  getOffers: () => string[];

  // Prospect Lists
  prospectLists: ProspectList[];
  prospectListsLoading: boolean;
  addProspectList: (listData: Omit<ProspectListInsert, 'id' | 'user_id' | 'created_at' | 'updated_at'> & { prospects: Prospect[] }) => Promise<ProspectList>;
  updateProspectList: (id: string, updates: Partial<ProspectList>) => Promise<ProspectList>;
  deleteProspectList: (id: string) => Promise<void>;
  acknowledgeTCPA: (listId: string, acknowledgedBy?: string) => Promise<ProspectList>;
  addProspectsToList: (listId: string, prospects: Prospect[]) => Promise<ProspectList>;
  removeProspectFromList: (listId: string, prospectId: string) => Promise<ProspectList>;
  updateProspectInList: (listId: string, prospectId: string, updates: Partial<Prospect>) => Promise<ProspectList>;
  refreshProspectLists: () => Promise<void>;
  getCompanies: () => string[];

  // Manual refresh for both
  refreshAll: () => Promise<void>;
}

const OptimizedCampaignDataContext = createContext<OptimizedCampaignDataContextType | undefined>(undefined);

export function useOptimizedCampaignData() {
  const context = useContext(OptimizedCampaignDataContext);
  if (context === undefined) {
    throw new Error('useOptimizedCampaignData must be used within an OptimizedCampaignDataProvider');
  }
  return context;
}

interface OptimizedCampaignDataProviderProps {
  children: ReactNode;
}

export function OptimizedCampaignDataProvider({ children }: OptimizedCampaignDataProviderProps) {
  const {
    scripts,
    templates,
    loading: scriptsLoading,
    addScript,
    updateScript,
    deleteScript,
    refreshScripts,
    getIndustries,
    getOffers,
  } = useOptimizedSupabaseScripts();

  const {
    prospectLists,
    loading: prospectListsLoading,
    addProspectList,
    updateProspectList,
    deleteProspectList,
    acknowledgeTCPA,
    refreshProspectLists,
    getCompanies,
  } = useSupabaseProspectLists();

  const refreshAll = async () => {
    await Promise.all([refreshScripts(), refreshProspectLists()]);
  };

  // Prospect management functions
  const addProspectsToList = async (listId: string, prospects: Prospect[]): Promise<ProspectList> => {
    const list = prospectLists.find(l => l.id === listId);
    if (!list) throw new Error('List not found');
    
    const updatedProspects = [...list.prospects, ...prospects];
    return updateProspectList(listId, { prospects: updatedProspects });
  };

  const removeProspectFromList = async (listId: string, prospectId: string): Promise<ProspectList> => {
    const list = prospectLists.find(l => l.id === listId);
    if (!list) throw new Error('List not found');
    
    const updatedProspects = list.prospects.filter(p => p.id !== prospectId);
    return updateProspectList(listId, { prospects: updatedProspects });
  };

  const updateProspectInList = async (listId: string, prospectId: string, updates: Partial<Prospect>): Promise<ProspectList> => {
    const list = prospectLists.find(l => l.id === listId);
    if (!list) throw new Error('List not found');
    
    const updatedProspects = list.prospects.map(p => 
      p.id === prospectId ? { ...p, ...updates } : p
    );
    return updateProspectList(listId, { prospects: updatedProspects });
  };

  const value: OptimizedCampaignDataContextType = {
    scripts,
    templates,
    scriptsLoading,
    addScript,
    updateScript,
    deleteScript,
    refreshScripts,
    getIndustries,
    getOffers,
    prospectLists,
    prospectListsLoading,
    addProspectList,
    updateProspectList,
    deleteProspectList,
    acknowledgeTCPA,
    addProspectsToList,
    removeProspectFromList,
    updateProspectInList,
    refreshProspectLists,
    getCompanies,
    refreshAll,
  };

  return (
    <OptimizedCampaignDataContext.Provider value={value}>
      {children}
    </OptimizedCampaignDataContext.Provider>
  );
}